import React, { ReactElement, useEffect } from 'react'

import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectProps } from '@mui/material/Select'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchStates,
  selectStates,
  selectStatesStatus,
  StatesResponse,
} from '../../settings/settings.slice'

const StateDropdown = ({
  name = 'state',
  value,
  label = 'State',
  variant = 'standard',
  onChange,
  onBlur,
  error = false,
  errorText,
  disabled,
  bgDefault,
  bgDefaultColor,
}: SelectProps<string> & {
  errorText?: string
  bgDefault?: boolean
  bgDefaultColor?: string
}): ReactElement => {
  let dispatch = useAppDispatch()

  let states = useAppSelector(selectStates)
  let statesStatus = useAppSelector(selectStatesStatus)

  useEffect(() => {
    if (statesStatus === 'uninitialized') {
      dispatch(fetchStates())
    }
  }, [statesStatus])

  let categories: (keyof StatesResponse)[] = [
    'States',
    'Territories',
    'Miscellaneous',
    'Countries',
  ]

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <InputLabel
        sx={variant === 'standard' ? { ml: -1.75, mt: 1 } : undefined}
      >
        {label}
      </InputLabel>
      <Select
        sx={{
          bgcolor: !bgDefault
            ? bgDefaultColor || 'background.paperDark'
            : undefined,
        }}
        variant={variant}
        value={value}
        label={label}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
      >
        <MenuItem value="unselected">Unselected</MenuItem>
        {categories.map((category) => {
          // If you use a Fragment with a nested map, the Select will not recognize any of the
          // options as being in range, so this somewhat tortured but nevertheless functional
          // solution was chosen.
          let options = !states?.[category]
            ? []
            : ['header', ...states[category]]

          return options.map((state, i) =>
            !i ? (
              <Box
                component="optgroup"
                label={category}
                sx={{
                  pr: 2,
                  py: 0.5,
                  pl: 3,
                  mt: 0.5,
                  color: 'text.secondary',
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: 'divider',
                }}
              />
            ) : (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ),
          )
        })}
      </Select>
      <FormHelperText>{errorText}</FormHelperText>
    </FormControl>
  )
}

export default StateDropdown
